/**
 * The in-memory match setup: teams, squads, toss, RuleSet.
 *
 * #10 has no server and no persistence, so this is the whole of "match creation".
 * It was shaped to line up with `CreateMatchRequest` in the API contract, and #14 duly
 * wired it to `POST /api/matches` by mapping fields rather than rewriting the screen —
 * see `publish.ts`, which is also where the local ids are exchanged for the server's.
 */
import { asId, isBatterHand, isBowlingArm, isBowlingType } from "@sportsos/engine";
import type {
  BatterHand,
  BowlingArm,
  BowlingType,
  MatchId,
  Player,
  PlayerId,
  PlayerStyleField,
  RuleSet,
  RuleSetId,
  TeamId,
  UserId,
} from "@sportsos/engine";
import type { BallType } from "./formats.ts";
import { newMatchId, newTeamId } from "./ids.ts";

/**
 * The one RuleSet P0 ships. There is no editing UI (PRD MS-5 is P1).
 *
 * Mirrors the `T20 v1` row seeded by migrations `0001` and `0003`, including the
 * pinned id — a match references a RuleSet *by id* so that a later rule edit cannot
 * retroactively rewrite a completed scorecard (PRD §6.5).
 *
 * **The id here is a placeholder and not a valid uuid**, deliberately: the real one comes
 * from `GET /api/rulesets` (see `listRuleSets` in packages/db for why it cannot be a
 * constant) and replaces this whole object before a match is created. What survives is the
 * shape, for the moment between opening the screen and that response landing.
 */
export const T20: RuleSet = {
  id: asId<RuleSetId>("00000000-0000-7000-8000-0000000000t2"),
  name: "T20",
  version: 1,
  playersPerSide: 11,
  overs: 20,
  ballsPerOver: 6,
  inningsPerSide: 1,
  maxWickets: null,
  maxOversPerBowler: 4,
  consecutiveOversAllowed: false,
  lbwEnabled: true,
  noBallFreeHit: true,
  wideRule: "standard",
  byesBreakMaiden: false,
  newBatterTakesStrikeOnCaught: true,
  superOverOnTie: true,
  lastManStands: false,
  oneTipOneHandOut: false,
  oneBounceCatchOut: false,
  retiredReturnsAllowed: true,
};

export interface SquadPlayer {
  readonly id: PlayerId;
  /** A name is the only required field. No phone number, ever (PRD ID-3). */
  readonly name: string;
  readonly battingOrder: number;
  readonly inXi: boolean;
  /**
   * What the player is known to do, as last recorded (ADR-0023). These are **defaults**: they
   * pre-fill the walk-in and bowler sheets so a known player costs the scorer nothing. What was
   * true in this match is in the ledger, and nothing on a scorecard reads these. Absent means not
   * known yet, which is asked once and never guessed.
   */
  readonly battingHand?: BatterHand;
  readonly bowlingArm?: BowlingArm;
  readonly bowlingType?: BowlingType;
  /**
   * Which of those the player set himself on a claimed profile. A scorer's choice does not change
   * these on the profile (it still goes in this match's ledger), and the correction warning says
   * so instead of promising "future matches" it cannot keep.
   */
  readonly styleSelfSet?: readonly PlayerStyleField[];
  /**
   * True when `id` is a row the server already has, so publishing must not create it
   * again (see `publish.ts`).
   *
   * False or absent for a name typed in this session, which carries a locally-minted id
   * until the server issues the real one. Creating a duplicate row for a reused squad
   * member would fork one human's identity into a separate career record per match.
   */
  readonly existing?: boolean;
}

export interface TeamSetup {
  readonly id: TeamId;
  readonly name: string;
  readonly players: readonly SquadPlayer[];
  /**
   * Who keeps wicket. Needed because Appendix A marks the keeper with † *inside* the
   * fielder list — `st †Khan b Sharma` — and `Delivery.keeperId` is the only thing
   * that tells the fold which of the named fielders that is.
   */
  readonly keeperId: PlayerId | null;
  readonly captainId: PlayerId | null;
  /** True when this team was chosen from the saved list rather than named here. */
  readonly existing?: boolean;
}

export interface MatchSetup {
  readonly matchId: MatchId;
  readonly ruleSet: RuleSet;
  /**
   * Who is scoring. Stamped onto every event's `Provenance` (see `draft.ts`).
   *
   * Nullable because authorisation is against a user id and credentials are optional by
   * design (ADR-0008) — that is what keeps guest scoring in P1 (ID-6) a policy change at
   * the login gate rather than a refactor of the ledger.
   */
  readonly scorerId: UserId | null;
  readonly teams: readonly [TeamSetup, TeamSetup];
  readonly tossWinnerTeamId: TeamId;
  readonly tossDecision: "bat" | "bowl";

  /**
   * Where the match is played, as typed. Autocompleted from the grounds this device has
   * used before (`venues.ts`).
   *
   * Local to the draft, because `CreateMatchRequest` carries no venue — a `Fixture` has one
   * and a standalone match does not (#38's contract), so there is nowhere to send it. It is
   * still worth holding: it is on the scorer's own screen, and it is what a tournament
   * fixture will be matched against in #44.
   */
  readonly venue?: string | null;
  /** ISO 8601, defaulting to when setup was opened. Local for the same reason as `venue`. */
  readonly scheduledAt?: string | null;
  /** Leather, tennis or tape. Defaults from the chosen format (`formats.ts`). */
  readonly ballType?: BallType;
  /**
   * Whether the match appears in the public live index. Sent as `public` on creation.
   *
   * On by default: discovery is the point of the home screen's live section, and a scorer
   * who wants a private game says so with one tap. Optional here rather than required so a
   * draft written by an older build still reads back.
   */
  readonly public?: boolean;
}

export const emptyTeam = (name: string): TeamSetup => ({
  id: newTeamId(),
  name,
  players: [],
  keeperId: null,
  captainId: null,
});

export const blankSetup = (): MatchSetup => {
  const teams: readonly [TeamSetup, TeamSetup] = [emptyTeam(""), emptyTeam("")];
  return {
    matchId: newMatchId(),
    ruleSet: T20,
    scorerId: null,
    teams,
    tossWinnerTeamId: teams[0].id,
    tossDecision: "bat",
    venue: null,
    scheduledAt: null,
    ballType: "leather",
    // Public unless the scorer says otherwise. See the field's note.
    public: true,
  };
};

/** The toss winner bats unless they chose to bowl. */
export function battingFirstTeamId(setup: MatchSetup): TeamId {
  const [a, b] = setup.teams;
  const loser = setup.tossWinnerTeamId === a.id ? b.id : a.id;
  return setup.tossDecision === "bat" ? setup.tossWinnerTeamId : loser;
}

export const otherTeamId = (setup: MatchSetup, teamId: TeamId): TeamId =>
  setup.teams[0].id === teamId ? setup.teams[1].id : setup.teams[0].id;

export const teamById = (setup: MatchSetup, teamId: TeamId): TeamSetup =>
  setup.teams[0].id === teamId ? setup.teams[0] : setup.teams[1];

/** The XI, in batting order. Squad members outside the XI cannot bat or bowl. */
export const xi = (team: TeamSetup): readonly SquadPlayer[] =>
  team.players.filter((player) => player.inXi).slice().sort((a, b) => a.battingOrder - b.battingOrder);

/**
 * Every player in the match, by id.
 *
 * `format.ts` takes a `NameLookup` rather than reading names itself, because the
 * engine only ever holds `PlayerId`s — names are the caller's problem, which is what
 * keeps the fold free of display concerns.
 */
export function nameLookup(setup: MatchSetup): (playerId: PlayerId) => string {
  const names = new Map<string, string>();
  for (const team of setup.teams) {
    for (const player of team.players) names.set(player.id, player.name);
  }
  return (playerId) => names.get(playerId) ?? "unknown";
}

/**
 * What still stops this setup being scoreable, or null when it is ready.
 *
 * Returns the *first* problem rather than a list: the setup screen shows one thing to
 * fix at a time, and a wall of validation is how a 60-second setup becomes a
 * three-minute one (PRD MS-1).
 */
/**
 * Give a player the captaincy or the gloves, taking it from whoever had it. Tapping the holder
 * again clears it. Only a member of the XI can hold either role.
 */
export function toggleRole(team: TeamSetup, role: "captain" | "keeper", playerId: PlayerId): TeamSetup {
  const field = role === "captain" ? "captainId" : "keeperId";
  if (team[field] === playerId) return { ...team, [field]: null };
  const inXi = team.players.some((player) => player.id === playerId && player.inXi);
  return inXi ? { ...team, [field]: playerId } : team;
}

/**
 * Move a player between the XI and the bench. Benching the captain or keeper takes the role
 * away, because neither can be held from the bench, and the "No captain" badge then says so.
 */
export function toggleInXi(team: TeamSetup, playerId: PlayerId): TeamSetup {
  const player = team.players.find((entry) => entry.id === playerId);
  if (player === undefined) return team;
  const benching = player.inXi;
  return {
    ...team,
    players: team.players.map((entry) => (entry.id === playerId ? { ...entry, inXi: !entry.inXi } : entry)),
    captainId: benching && team.captainId === playerId ? null : team.captainId,
    keeperId: benching && team.keeperId === playerId ? null : team.keeperId,
  };
}

/**
 * Whether a newly added player can go straight into the XI. A squad is unlimited, so once the
 * XI is full a new name lands on the bench instead of silently making a twelve-man side.
 */
export const hasXiRoom = (team: TeamSetup, playersPerSide: number): boolean =>
  xi(team).length < playersPerSide;

export function setupProblem(setup: MatchSetup, options?: { requireCaptain?: boolean }): string | null {
  const [a, b] = setup.teams;
  const requireCaptain = options?.requireCaptain ?? true;
  for (const team of [a, b]) {
    if (team.name.trim() === "") return "Both teams need a name";
    if (xi(team).length < 3) return `${team.name || "Each team"} needs at least 3 in the XI`;
    if (xi(team).length > setup.ruleSet.playersPerSide) {
      return `${team.name || "Each team"} has ${xi(team).length} in the XI — at most ${setup.ruleSet.playersPerSide}`;
    }
    if (requireCaptain && team.captainId === null) return `${team.name || "Each team"} needs a captain — tap a player to set`;
    if (requireCaptain && team.captainId !== null) {
      const captainInXi = xi(team).some((p) => p.id === team.captainId);
      if (!captainInXi) return `${team.name}'s captain must be in the XI`;
    }
  }
  if (a.name.trim() === b.name.trim()) return "The two teams need different names";
  return null;
}

/**
 * The saved defaults a squad member carries into a match (ADR-0023), from the server's player.
 *
 * Only what is known: a field is left off rather than set to `undefined` (the types forbid it),
 * and a value that is not one of the allowed ones is dropped, because a cached response from
 * another build is whatever it was.
 */
export function savedDefaults(
  player: Pick<Player, "battingHand" | "bowlingArm" | "bowlingType" | "styleSelfSet">,
): Pick<SquadPlayer, "battingHand" | "bowlingArm" | "bowlingType" | "styleSelfSet"> {
  const selfSet = (player.styleSelfSet ?? []).filter(
    (field) => field === "battingHand" || field === "bowlingArm" || field === "bowlingType",
  );
  return {
    ...(selfSet.length === 0 ? {} : { styleSelfSet: selfSet }),
    ...(isBatterHand(player.battingHand) ? { battingHand: player.battingHand } : {}),
    ...(isBowlingArm(player.bowlingArm) ? { bowlingArm: player.bowlingArm } : {}),
    ...(isBowlingType(player.bowlingType) ? { bowlingType: player.bowlingType } : {}),
  };
}
